import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { backendApis } from "../../config";
import { useAuth } from "../../hooks/useAuth";
import eventBus from "../../utils/EventBus";
import {
  formDataForFormBuider,
  formatDate,
  formatNumberSystem,
  waitFor,
} from "../../utils/helpers";
import { cacheTimes, queryActions } from "../../utils/reactQueryActions";
import ConfirmDialog from "../ConfirmDialogue";
import { Logo } from "../Icons";
import ModalLoader from "../utils/ModalLoader";
import { useStoreState } from "easy-peasy";
import { isEmpty } from "lodash";
import { useLatestRef } from "../../utils/hooks";
import ValidationSummarySection from "./ValidationSummarySection";
import { validateForm } from "../../utils/validator";

export default function FillFormModal({
  openFilledForm,
  setOpenFilledForm,
  mainFormID,
  name,
  setthefilledForm,
  stageId,
  stageFormId,
  onFilledFormSuccess,
}) {
  const { user } = useAuth();
  const authUser = user;

  //  get company from  url,  because in  public  form  the user  is  not logged  in
  const { url: backendUrl } = backendApis.find(
    el => el?.name === user?.company
  );

  const iframeRef = useRef();
  const currentLoggedInCompany = useStoreState(
    state => state.currentLoggedInCompany
  );
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const formik = useFormik({
    initialValues: {
      FormID: mainFormID,
      UserName: user?.Name || "",
      Staff_ID: user?.Staff_ID || "",
      filledFormData: "",
      formName: "",
      poNumber: "",
    },
    validationSchema: yup.object().shape({
      formName: yup.string().required(""),
    }),
    onSubmit: async values => {
      if (name === "Purchase Order" && isEmpty(values.poNumber)) {
        return toast.error("Please enter PO Number");
      }

      fillFormMutation.mutate({ ...values });
    },
  });

  const fillForm = async payload => {
    //  console.log(payload);
    payload.Template = payload.filledFormData;
    payload.stageId = stageId;
    payload.stageFormId = stageFormId;
    const formData = formDataForFormBuider(payload);

    let response = await fetch(`${backendUrl}/api/forms/fill-form`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const fillFormMutation = useMutation(payload => fillForm(payload), {
    onSuccess: ({ message, data }) => {
      if (onFilledFormSuccess) {
        onFilledFormSuccess(data);
      }

      toast.success(message);
      setthefilledForm(data);
      setOpenFilledForm(false);
    },
    onError: () => {
      toast.error(`Unable to perform action`);
    },
  });

  const setformData = async data => {
    formik.setFieldValue("filledFormData", data);

    const validationResult = validateForm(data);

    if (!validationResult?.isValid) {
      ConfirmDialog({
        title: "Missing Required Fields",
        description: (
          <ValidationSummarySection
            report={validationResult}
            isOpenByDefault={true}
          />
        ),
        type: "danger",
        choice: false,
      });
      return;
    }

    if (
      await ConfirmDialog({
        title: "Submit",
        description: "Are you sure, you want to submit",
      })
    ) {
      formik.submitForm();
    }
  };

  //------------------------
  //  GEt Form  for filling
  const getForm = async () => {
    let response = await fetch(
      `${backendUrl}/api/forms/get-form/${mainFormID}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    if (data?.form.Title) {
      formik.setFieldValue("formName", data?.form.Title);
    }

    if (!data?.form?.enabled) {
      setIsLoadingIframe(false);
    }
    return data;
  };
  const getFormQuery = useQuery(["GET_FORM"], () => getForm(), {
    enabled: true,
  });

  //----------------------------------------------------------------------
  // fetch data for form
  const getDepartments = async () => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/users/departments-with-users`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };
  const {
    data = { departments: [], staffs: [], jobGrades: [] },
    isFetching,
  } = useQuery(
    [queryActions.GET_USERS_IN_DEPARTMENTS],
    () => getDepartments(),
    {
      enabled: true,
      keepPreviousData: true,
      cacheTime: cacheTimes.GET_USERS_WITH_DEPARTMENTS,
    }
  );

  const setOrientation = async printOrientation => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "OREINTATION",
          data: {
            ...printOrientation,
          },
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  // Add refs to track latest values
  const dataRef = useLatestRef(data);
  const getFormQueryRef = useLatestRef(getFormQuery);

  // Update sendSetupData to use refs
  const sendSetupData = async () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const currentData = dataRef.current;
      const currentFormQuery = getFormQueryRef.current;

      iframe.contentWindow.postMessage(
        {
          data: {
            ...currentData,
            template: JSON.parse(currentFormQuery.data.form.Template),
            backendUrl,
            companyData: currentLoggedInCompany,
            formInfo: {
              formNumber: formatNumberSystem(
                currentFormQuery.data.form.formNumberPrefix,
                currentFormQuery.data.form.currentNumber
              ),
              documentNumber: currentFormQuery.data.form?.documentNumber,
              revision: currentFormQuery.data.form.revision,
              date: formatDate(currentFormQuery.data.form.Date_Log, "MMM yyyy"),
              Title: currentFormQuery.data.form?.Title,
            },
            authUser,
          },
          settings: { canFill: true, showTableCheckbox: false },
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  const handleMessage = message => {
    if (message.data.type === "CLOSE") {
      //
    } else if (message.data.type === "SAVE") {
      setformData(message.data.value);
    } else if (message.data.type === "FORM_BUILDER_MOUNTED") {
      if (getFormQueryRef.current.data?.form) {
        sendSetupData();
        setOrientation({
          orientation: getFormQueryRef.current.data?.form.orientation,
          pageSize: getFormQueryRef.current.data?.form.pageSize,
        });
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    // cleanup this component
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const save = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SUBMIT",
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  eventBus.useCustomEventListener(
    "SEND_FILE_TO_IFRAME",
    ({ files, elementId, elementType }) => {
      console.log(files, elementId, elementType);
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            data: {
              files,
              elementId,
              elementType,
            },
            type: "FILES_FROM_PARENT",
          },
          process.env.REACT_APP_FORM_BUILDER_URL
        );
      }
    }
  );

  return (
    <>
      <Modal
        show={openFilledForm}
        onHide={() => setOpenFilledForm(false)}
        centered={true}
        animation={false}
        enforceFocus={false}
        backdrop="static"
        size="xl"
        dialogClassName={`${
          getFormQuery?.data?.form.orientation === "Landscape"
            ? "modal-90w"
            : ""
        }`}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h1 className="h5 m-0">
              {" "}
              {stageFormId ? "" : "Create"} {name}
            </h1>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="w-100 row d-flex justify-content-center">
            <Form.Group className="col-md-5">
              <div className="p-4 py-3  pt-0 text-center mx-auto">
                <label for="formname">
                  <b>Enter Form Name</b> <span className="text-danger">*</span>
                </label>
                <Form.Control
                  name="formName"
                  placeholder="Form Name"
                  required
                  value={formik.values.formName}
                  onChange={formik.handleChange}
                  isInvalid={
                    formik.touched.formName && !!formik.errors.formName
                  }
                />{" "}
                <Form.Control.Feedback type="invalid">
                  {formik.errors.formName}
                </Form.Control.Feedback>
              </div>
            </Form.Group>
            {name === "Purchase Order" && (
              <Form.Group className="col-md-3">
                <div className="p-3 py-3 pt-0 text-center  mx-auto">
                  <label for="formname">
                    <b>PO Number</b> <span className="text-danger">*</span>
                  </label>
                  <Form.Control
                    name="poNumber"
                    placeholder="PO Number"
                    required
                    value={formik.values.poNumber}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.poNumber && !!formik.errors.poNumber
                    }
                  />{" "}
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.poNumber}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            )}
          </div>
          {!isFetching && !getFormQuery.isFetching ? (
            getFormQuery?.data?.form?.enabled ? (
              <>
                <iframe
                  ref={iframeRef}
                  id="Frame"
                  onLoad={e => {
                    setIsLoadingIframe(false);
                  }}
                  className="w-100 border"
                  style={{
                    backgroundColor: "#E5E7EB",
                    minHeight: "calc(100vh - 20rem)",
                  }}
                  title="Form Builder"
                  src={`${process.env.REACT_APP_FORM_BUILDER_URL}/form/fill?v=${
                    process.env?.REACT_APP_VERSION
                  }`}
                />
                <Form noValidate onSubmit={formik.handleSubmit}>
                  <input type={"hidden"} value={formik.values.FormID} />
                </Form>
                <div className="action-area w-100 px-3 pt-3 text-center">
                  <Button
                    size="lg"
                    className="px-5"
                    type="button"
                    onClick={() => save()}
                    disabled={!getFormQuery?.data?.form?.enabled}
                  >
                    Submit
                  </Button>
                </div>
              </>
            ) : (
              <div className="vh-100 vw-100 d-flex align-items-center justify-content-center text-center">
                <div>
                  <Logo />
                  <h1 className="display-5">Form Disabled</h1>
                  <p className="">...</p>
                </div>
              </div>
            )
          ) : null}{" "}
        </Modal.Body>
      </Modal>

      <ModalLoader
        show={isLoadingIframe || fillFormMutation.isLoading || isFetching}
      />
    </>
  );
}
